import { useContext, useEffect, useState } from "react";
import { AuthContext } from "./AuthContext";
import { onValue, ref } from "firebase/database";
import { database } from "./firebase";
import { filterTicketsByRole } from "./filterTickets";

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from "recharts";

const Overview = () => {
  const { role, userId } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    const tRef = ref(database, "/helpdesk/tickets");

    const unsubT = onValue(tRef, (snapshot) => {
      const data = snapshot.val();

      if (data) {
        let list = Object.keys(data).map((id) => ({
          id,
          ...data[id],
          progress: data[id].progress || 0,
          assignedUsers: data[id].assignedUsers || [],
        }));

        list = filterTicketsByRole(list, userId, role);
        setTickets(list);
      } else {
        setTickets([]);
      }
    });

    return () => unsubT();
  }, [role, userId]);

  // ---------------------------
  //   CALCULATIONS
  // ---------------------------
  const total = tickets.length;
  const pending = tickets.filter((t) => t.status !== "Resolved").length;
  const resolved = tickets.filter((t) => t.status === "Resolved").length;
  const postDue = tickets.filter(
    (t) =>
      t.deadline && new Date(t.deadline) < new Date() && t.status !== "Resolved"
  ).length;

  // Pie chart: priority distribution
  const priorityData = [
    { name: "Low", value: tickets.filter((t) => t.priority === "Low").length },
    {
      name: "High",
      value: tickets.filter((t) => t.priority === "High").length,
    },
    {
      name: "Urgent",
      value: tickets.filter((t) => t.priority === "Urgent").length,
    },
  ];

  // Bar chart: last 7 days
  const days = [...Array(7)].map((_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    const short = date.toLocaleDateString("en-US", { weekday: "short" });

    const count = tickets.filter((t) => {
      const raised = new Date(t.timeRaised);
      return (
        raised.getFullYear() === date.getFullYear() &&
        raised.getMonth() === date.getMonth() &&
        raised.getDate() === date.getDate()
      );
    }).length;

    return { day: short, tickets: count };
  });

  // Department data (only admin)
  const departmentData = [];
  if (role === "admin") {
    const departments = [
      ...new Set(tickets.map((t) => t.department || "Unknown")),
    ];

    departments.forEach((dept) => {
      const deptTickets = tickets.filter(
        (t) => (t.department || "Unknown") === dept
      );

      const resolvedCount = deptTickets.filter(
        (t) => t.status === "Resolved"
      ).length;
      const pendingCount = deptTickets.filter(
        (t) => t.status !== "Resolved"
      ).length;
      const postDueCount = deptTickets.filter(
        (t) =>
          t.deadline &&
          new Date(t.deadline) < new Date() &&
          t.status !== "Resolved"
      ).length;

      departmentData.push({
        department: dept,
        Resolved: resolvedCount,
        Pending: pendingCount,
        PostDue: postDueCount,
      });
    });
  }

  // ---------------------------
  //   CATEGORY DATA
  // ---------------------------
  const categoryData = [];
  const categories = [
    ...new Set(tickets.map((t) => t.category || "Uncategorized")),
  ];

  categories.forEach((cat) => {
    const count = tickets.filter(
      (t) => (t.category || "Uncategorized") === cat
    ).length;
    categoryData.push({ name: cat, value: count });
  });

  // ---------------------------
  //   Stats Card Component
  // ---------------------------
  const StatsCard = ({ iconClass, value, label, bgColor }) => {
    return (
      <div className="card shadow mb-3">
        <div className="card-header bg-white d-flex justify-content-center">
          <div
            style={{
              height: "48px",
              width: "48px",
              borderRadius: "50%",
              backgroundColor: bgColor || "#6c757d",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i
              className={`fa ${iconClass} text-white`}
              style={{ fontSize: "24px" }}
            ></i>
          </div>
        </div>
        <div className="card-body text-center">
          <h4 className="text-gray-800">{label}</h4>
          <h2 className="text-gray-400">{value}</h2>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* STATS CARDS */}
      <div className="row g-3 mt-5">
        <div className="col-md-3">
          <StatsCard
            iconClass="fa-life-ring"
            value={total}
            label="Total Tickets"
            bgColor="#09b3e9"
          />
        </div>
        <div className="col-md-3">
          <StatsCard
            iconClass="fa-hourglass-half"
            value={pending}
            label="Pending"
            bgColor="#ff9800"
          />
        </div>
        <div className="col-md-3">
          <StatsCard
            iconClass="fa-check-circle"
            value={resolved}
            label="Resolved"
            bgColor="#2196f3"
          />
        </div>
        <div className="col-md-3">
          <StatsCard
            iconClass="fa-exclamation-triangle"
            value={postDue}
            label="Post Due"
            bgColor="#f44336"
          />
        </div>
      </div>

      {/* CHARTS */}
      <div className="row mt-3">
        {/* PRIORITY PIE CHART */}
        <div className="col-md-6">
          <div className="card shadow p-3">
            <h5 className="text-gray-800">Priority Distribution</h5>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  dataKey="value"
                  data={priorityData}
                  outerRadius={110}
                  label
                >
                  <Cell fill="#f8f9fc" /> {/* Low */}
                  <Cell fill="#ff9800" /> {/* High */}
                  <Cell fill="#f44336" /> {/* Urgent */}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* LAST 7 DAYS BAR CHART */}
        <div className="col-md-6">
          <div className="card shadow p-3">
            <h5 className="text-gray-800">Tickets Raised (Last 7 Days)</h5>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={days}>
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="tickets" fill="#09b3e9" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* DEPARTMENT STACKED CHART (ADMIN ONLY) */}
      {role === "admin" && (
        <div className="row mt-3">
          <div className="col-12">
            <div className="card shadow p-3">
              <h5 className="text-gray-800">Tickets by Department</h5>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={departmentData}
                  margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                >
                  <XAxis dataKey="department" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="Resolved" stackId="a" fill="#2196f3" />
                  <Bar dataKey="Pending" stackId="a" fill="#ff9800" />
                  <Bar dataKey="PostDue" stackId="a" fill="#f44336" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* CATEGORY PIE CHART */}
      <div className="row mt-3">
        <div className="col-md-6">
          <div className="card shadow p-3">
            <h5 className="text-gray-800">Tickets by Category</h5>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  dataKey="value"
                  data={categoryData}
                  outerRadius={110}
                  label
                >
                  {categoryData.map((entry, index) => {
                    // Assign colors dynamically
                    const colors = [
                      "#6f42c1",
                      "#ff9800",
                      "#f44336",
                      "#2196f3",
                      "#9c27b0",
                      "#00bcd4",
                    ];
                    return (
                      <Cell
                        key={`cell-${index}`}
                        fill={colors[index % colors.length]}
                        fillOpacity={0.7} // Set desired opacity (0.0 to 1.0)
                      />
                    );
                  })}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </>
  );
};

export default Overview;
